import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link as RouterLink, useNavigate, useParams, useSearchParams} from 'react-router';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link as MuiLink,
    Paper,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {useEntity} from '../../hooks/useEntity';
import {useEntityClaims} from '../../hooks/useEntityClaims';
import {useEntityMentions} from '../../hooks/useEntityMentions';
import {useEntityDataItems} from '../../hooks/useEntityDataItems';
import {useEntityProperties} from '../../hooks/useEntityProperties';
import {useEntityAlerts} from '../../hooks/useEntityAlerts';
import {useEntityPropertyMutations} from '../../hooks/useEntityPropertyMutations';
import {useEntityAliasMutations} from '../../hooks/useEntityAliasMutations';
import {useEntityAliases} from '../../hooks/useEntityAliases';
import {useCarver, useCarverHistory} from '../../hooks/useCarver';
import {useCarverConfig} from '../../hooks/useCarverConfig';
import {useCarverMutations} from '../../hooks/useCarverMutations';
import {useEntityNotes, useEntityNoteMutations} from '../../hooks/useEntityNotes';
import {useEntityPersonnel} from '../../hooks/useEntityPersonnel';
import {useEntityLinks} from '../../hooks/useEntityLinks';
import {useEntityCollectionRequirements} from '../../hooks/useEntityCollectionRequirements';
import {useEntityIntelligenceSummary} from '../../hooks/useEntityIntelligenceSummary';
import {getWikidataUrl, isLocalEntityRef} from '../../utils/entityLinks';
import {bustEntityAvatarCacheUrl, resolveEntityAvatarUrl} from '../../utils/entityAvatarUrl';
import {uploadEntityAvatar} from '../../firebase/firestore/entityAvatar';
import {getEntityTypeLabel, isPlaceholderEntityType} from '../../utils/entityTypeMeta';
import {isAdminUser} from '../../components/entityMentions/MentionHoverCard';
import {useWikidataAutoFetch} from '../../hooks/useWikidataAutoFetch';
import UserMentionPopover from '../../components/prosemirror-editor/components/UserMentionPopover';
import {useUserMentionPopover} from '../../components/prosemirror-editor/hooks/useUserMentionPopover';
import {
    AlertsTab,
    AliasesTab,
    CarverTab,
    ClaimsTab,
    CollectionRequirementsTab,
    EntityChartsTab,
    DataItemsTab,
    EntityDescriptionEditor,
    GraphTab,
    MentionsTab,
    PersonnelTab,
    PropertiesTab,
    SummaryTab,
} from './components';
import PromoteEntityCandidateDialog from './components/PromoteEntityCandidateDialog';
import {kbAliasesCrudEnabled, kbPropertiesCrudEnabled} from './featureFlags';
import {deleteAdminKbEntity} from '../../utils/api/adminEntities';

// ---------------------------------------------------------------------------
// Tab label helper — shows count only when data has loaded
// ---------------------------------------------------------------------------
const tabLabel = (label, count, loading) =>
    loading ? label : `${label} (${count})`;

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const EntityDetailPage = () => {
    const {entityId} = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = Math.max(0, parseInt(searchParams.get('tab') ?? '0', 10) || 0);
    const setTab = useCallback((v) => setSearchParams(p => { const n = new URLSearchParams(p); n.set('tab', String(v)); return n; }, {replace: true}), [setSearchParams]);
    const [refetchKey, setRefetchKey] = useState(0);
    const isInitialMount = useRef(true);
    const [promoteOpen, setPromoteOpen] = useState(false);
    const [promoteVariant, setPromoteVariant] = useState('custom');
    const [promoteSuccessMsg, setPromoteSuccessMsg] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    useEffect(() => {
        if (isInitialMount.current) { isInitialMount.current = false; return; }
        setSearchParams(p => { const n = new URLSearchParams(p); n.delete('tab'); return n; }, {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    useEffect(() => {
        setLocalImageUrl('');
        setAvatarError(null);
    }, [entityId]);
    const [propsRefetchKey, setPropsRefetchKey] = useState(0);
    const [aliasesRefetchKey, setAliasesRefetchKey] = useState(0);
    const [carverRefetchKey, setCarverRefetchKey] = useState(0);
    const [notesRefetchKey, setNotesRefetchKey] = useState(0);
    // Notes authors feed Personnel; bump both after post so the tab badge refreshes.
    const [personnelRefetchKey, setPersonnelRefetchKey] = useState(0);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarError, setAvatarError] = useState(null);
    const [localImageUrl, setLocalImageUrl] = useState('');
    const avatarInputRef = useRef(null);

    const handleCarverMutated = useCallback(() => {
        setCarverRefetchKey(k => k + 1);
    }, []);

    const handleNotesMutated = useCallback(() => {
        setNotesRefetchKey(k => k + 1);
        setPersonnelRefetchKey(k => k + 1);
    }, []);

    const handlePropertiesHydrated = useCallback(() => {
        setPropsRefetchKey(k => k + 1);
    }, []);

    const handleAliasesMutated = useCallback(() => {
        setAliasesRefetchKey(k => k + 1);
        setRefetchKey(k => k + 1);
    }, []);

    const {
        anchorEl: mentionAnchorEl,
        userData: mentionUserData,
        open: mentionPopoverOpen,
        handleUserMentionClick,
        handleClose: handleMentionPopoverClose,
    } = useUserMentionPopover();

    const {entity, loading, error, notFound} = useEntity(entityId, refetchKey);
    const isLocalEntity = isLocalEntityRef(entityId) || entity?.local === true;
    const {
        properties,
        revision,
        loading: propsLoading,
        error: propsError,
    } = useEntityProperties(entityId, propsRefetchKey);
    const {isFetching, isFetchingProps, fetchError, fetched, notFoundOnWikidata, requestImport, retry, canRetry} =
        useWikidataAutoFetch(entityId, entity, loading, error, notFound, {
            propertiesLoading: propsLoading,
            propertiesCount: properties.length,
            onPropertiesHydrated: handlePropertiesHydrated,
            // Never auto-create on /kb/Q… — user must opt in (avoids recreating deleted entities).
            autoImportMissing: false,
        });
    // Local chart entities never hydrate from Wikidata; ignore auto-fetch UI for them.
    const showWikidataHydration = !isLocalEntity;
    const canOfferWikidataImport = showWikidataHydration
        && notFound
        && !notFoundOnWikidata
        && Boolean(entityId)
        && /^Q\d+$/i.test(String(entityId));


    // When auto-fetch completes, re-read the entity doc
    useEffect(() => {
        if (fetched) setRefetchKey(k => k + 1);
    }, [fetched]);

    const awaitingFirestoreHydration = fetched && !loading && !entity && !error && !fetchError;

    const {
        claims: fintelClaims,
        reportHeadersById: fintelReportHeadersById,
        loading: fintelLoading
    } = useEntityClaims(entityId, 'fintel');
    const {
        claims: draftClaims,
        reportHeadersById: draftReportHeadersById,
        loading: draftsLoading
    } = useEntityClaims(entityId, 'draft');
    const {alerts, loading: alertsLoading} = useEntityAlerts(entityId);
    const {mentions, loading: mentionsLoading} = useEntityMentions(entityId);
    const {dataItems, loading: dataItemsLoading} = useEntityDataItems(entityId);
    const {surfaceForms, wikidataAliases, loading: aliasesLoading, error: aliasesError} =
        useEntityAliases(entityId, aliasesRefetchKey);

    const {
        createProperty,
        updateProperty,
        deleteProperty,
        saving: propertyMutationSaving,
        error: propertyMutationError,
    } = useEntityPropertyMutations({
        entityId,
        revision,
        onMutationSuccess: handlePropertiesHydrated,
    });

    const {
        createAlias,
        deleteAlias,
        saving: aliasMutationSaving,
        error: aliasMutationError,
    } = useEntityAliasMutations({
        entityId,
        onMutationSuccess: handleAliasesMutated,
    });

    const {
        factors: carverFactors,
        eligible: carverEligible,
        revision: carverRevision,
        total: carverTotal,
        scoredFactorCount: carverScoredCount,
        loading: carverLoading,
        error: carverError,
    } = useCarver(entityId, carverRefetchKey);
    const {entries: carverHistory, loading: carverHistoryLoading} =
        useCarverHistory(entityId, carverRefetchKey);
    const {minScore: carverMinScore, maxScore: carverMaxScore} = useCarverConfig();
    const {
        setFactor: setCarverFactor,
        saving: carverSaving,
        error: carverMutationError,
    } = useCarverMutations({
        entityId,
        revision: carverRevision,
        onMutationSuccess: handleCarverMutated,
    });

    const {notes, loading: notesLoading, error: notesError} =
        useEntityNotes(entityId, notesRefetchKey);
    const {
        postNote,
        updateNote,
        deleteNote,
        saving: notesSaving,
        error: notesMutationError,
    } = useEntityNoteMutations({
        entityId,
        onMutationSuccess: handleNotesMutated,
    });
    const {personnel, loading: personnelLoading, error: personnelError} =
        useEntityPersonnel(entityId, personnelRefetchKey);
    const {
        charts: entityLinkCharts,
        loading: entityChartsLoading,
        error: entityChartsError,
    } = useEntityLinks(entityId);
    const {
        icrs: collectionIcrs,
        eeis: collectionEeis,
        gaps: collectionGaps,
        cts: collectionCts,
        cms: collectionCms,
        iirs: collectionIirs,
        totalCount: collectionTotal,
        loading: collectionLoading,
        error: collectionError,
    } = useEntityCollectionRequirements(entityId);

    // Only signed-in users may score/post/edit summary: the API records who changed what.
    const carverCanEdit = !!localStorage.getItem('access_token');
    const notesCanPost = carverCanEdit;
    const summaryCanEdit = carverCanEdit;
    const entityCanEdit = carverCanEdit;

    const handleAvatarSelected = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !entityId) return;

        setAvatarUploading(true);
        setAvatarError(null);
        try {
            const {imageUrl} = await uploadEntityAvatar(entityId, file);
            setLocalImageUrl(bustEntityAvatarCacheUrl(imageUrl));
            setRefetchKey((value) => value + 1);
        } catch (err) {
            setAvatarError(err);
        } finally {
            setAvatarUploading(false);
        }
    }, [entityId]);

    /*
     * Tab index map (EntityDetailPage ?tab=):
     * 0 Summary (notes thread embedded below summary)
     * 1 In FINTEL, 2 In Drafts, 3 Corpus, 4 Files, 5 Collection, 6 Charts
     * 7 Involvement, 8 Personnel, 9 Properties, 10 Alerts, 11 Aliases, 12 CARVER (when eligible)
     *
     * Breaking change: legacy Notes tab URLs (?tab=11) are no longer valid after Notes moved to Summary.
     */
    const lastTabIndex = carverEligible ? 12 : 11;
    const activeTab = tab > lastTabIndex ? 0 : tab;
    const summaryTabActive = activeTab === 0;

    const {
        summary: intelligenceSummary,
        cached: intelligenceSummaryCached,
        stale: intelligenceSummaryStale,
        generatedAt: intelligenceSummaryGeneratedAt,
        sourceCounts: intelligenceSourceCounts,
        loading: intelligenceSummaryLoading,
        regenerating: intelligenceSummaryRegenerating,
        saving: intelligenceSummarySaving,
        error: intelligenceSummaryError,
        manuallyEdited: intelligenceSummaryManuallyEdited,
        regenerate: regenerateIntelligenceSummary,
        regenerateAfterSourceChange,
        saveSummary: saveIntelligenceSummary,
    } = useEntityIntelligenceSummary(entityId, {enabled: summaryTabActive});

    const handleCreateProperty = useCallback(async (payload) => {
        const result = await createProperty(payload);
        regenerateAfterSourceChange();
        return result;
    }, [createProperty, regenerateAfterSourceChange]);

    const handleUpdateProperty = useCallback(async (propertyItemId, payload) => {
        const result = await updateProperty(propertyItemId, payload);
        regenerateAfterSourceChange();
        return result;
    }, [regenerateAfterSourceChange, updateProperty]);

    const handleDeleteProperty = useCallback(async (propertyItemId) => {
        const result = await deleteProperty(propertyItemId);
        regenerateAfterSourceChange();
        return result;
    }, [deleteProperty, regenerateAfterSourceChange]);

    const propertyCrudEnabled = kbPropertiesCrudEnabled;
    const aliasCrudEnabled = kbAliasesCrudEnabled;
    const propertyError = propertyCrudEnabled ? (propertyMutationError || propsError) : propsError;
    const aliasError = aliasCrudEnabled ? (aliasMutationError || aliasesError) : aliasesError;
    const aliasTabCount = wikidataAliases.length + surfaceForms.length;

    const currentEntityWikidataId = entity?.wikidataId || entityId;
    const entityHighlightContext = useMemo(() => ({
        wikidataId: currentEntityWikidataId,
        canonicalName: entity?.canonicalName,
        aliases: entity?.aliases,
        wikidataAliases,
        surfaceForms,
    }), [currentEntityWikidataId, entity?.canonicalName, entity?.aliases, wikidataAliases, surfaceForms]);

    const handleConfirmDeleteEntity = useCallback(async () => {
        if (!entityId) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await deleteAdminKbEntity(entityId);
            setDeleteConfirmOpen(false);
            navigate('/kb', {
                replace: true,
                state: {kbDeletedEntityName: entity?.canonicalName || entityId},
            });
        } catch (err) {
            setDeleteError(err);
        } finally {
            setDeleteBusy(false);
        }
    }, [entityId, entity?.canonicalName, navigate]);

    if (loading || (showWikidataHydration && (isFetching || awaitingFirestoreHydration))) {
        return (
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, gap: 2}}>
                <CircularProgress/>
                {showWikidataHydration && isFetching && (
                    <Typography color="text.secondary">
                        Fetching {entityId} from Wikidata…
                    </Typography>
                )}
                {showWikidataHydration && awaitingFirestoreHydration && (
                    <Typography color="text.secondary">
                        Loading entity from Firestore...
                    </Typography>
                )}
            </Box>
        );
    }

    if (error || (showWikidataHydration && fetchError)) {
        return (
            <Stack spacing={2} p={4} alignItems="center">
                <Typography color="error">
                    {showWikidataHydration && fetchError
                        ? 'Failed to fetch entity from Wikidata.'
                        : 'Failed to load entity.'}
                </Typography>
                {showWikidataHydration && fetchError && (
                    <Button variant="outlined" onClick={retry} disabled={!canRetry}>
                        {canRetry ? 'Retry' : 'Max retries reached'}
                    </Button>
                )}
                <Button component={RouterLink} to="/kb">Back to Knowledge Base</Button>
            </Stack>
        );
    }

    if (!entity) {
        return (
            <Stack spacing={2} p={4} alignItems="center" data-testid="kb-entity-missing">
                <Typography variant="h6">
                    {showWikidataHydration && notFoundOnWikidata
                        ? 'Not found on Wikidata'
                        : 'Not in Knowledge Base'}
                </Typography>
                <Typography color="text.secondary" textAlign="center" maxWidth={480}>
                    {showWikidataHydration && notFoundOnWikidata
                        ? `"${entityId}" was not found on Wikidata.`
                        : `“${entityId}” is not in the Knowledge Base. Pull it from Wikidata only if you want to add it as a candidate.`}
                </Typography>
                {canOfferWikidataImport && (
                    <Button
                        variant="contained"
                        startIcon={<AutoAwesomeIcon/>}
                        onClick={requestImport}
                        data-testid="kb-pull-wikidata-candidate"
                    >
                        Pull from Wikidata
                    </Button>
                )}
                <Button component={RouterLink} to="/kb">Back to Knowledge Base</Button>
            </Stack>
        );
    }

    const wikidataUrl = isLocalEntity ? null : getWikidataUrl(entity.wikidataId || entityId);
    const displayImageUrl = resolveEntityAvatarUrl({
        wikidataId: entity.wikidataId || entityId,
        imageUrl: localImageUrl || entity.image || null,
    });
    const isNerCandidate = isLocalEntity && entity.source === 'entities';
    const wikidataQid = String(entity.wikidataId || entityId || '');
    const isWikidataQid = /^Q\d+$/i.test(wikidataQid);
    // Wikidata adopt is only for untyped Q-id search hits (no NER type yet). Linked KB
    // entities (e.g. Donald Trump Q22686 / PERSON) are already in the Knowledge Base —
    // they must not show "Promote to Knowledge Base". Mentions (D-refs, no QID) use
    // isNerCandidate → "Promote to Custom Entity" instead.
    const isWikidataSearchCandidate = isWikidataQid && !isNerCandidate
        && isPlaceholderEntityType(entity.entityType);
    const showAdminAdopt = isAdminUser() && isWikidataSearchCandidate;
    const showAdminDelete = isAdminUser();

    return (
        <Stack width="100%" overflow="auto" pb={4}>
            <Stack maxWidth={1600} width="100%" mx="auto">
                {/* Back button */}
                <Box p={2} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Button
                        startIcon={<ArrowBackIcon/>}
                        component={RouterLink}
                        to="/kb"
                        sx={{alignSelf: 'flex-start'}}
                    >
                        Back to Knowledge Base
                    </Button>
                    {showAdminDelete && (
                        <Button
                            color="error"
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteOutlineIcon/>}
                            onClick={() => {
                                setDeleteError(null);
                                setDeleteConfirmOpen(true);
                            }}
                            data-testid="admin-remove-entity"
                        >
                            Remove from KB
                        </Button>
                    )}
                </Box>

                {/* ── Hero header ── */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        overflow: 'hidden',
                        mx: 2,
                    }}
                >

                    <Stack sx={{p: 3}} spacing={2}>
                        <Stack direction="row" spacing={3} alignItems="flex-end">
                            <Box sx={{position: 'relative', flexShrink: 0}}>
                                <Avatar
                                    src={displayImageUrl || undefined}
                                    alt={entity.canonicalName}
                                    sx={{
                                        width: 96, height: 96, fontSize: 40,
                                        border: '4px solid', borderColor: 'background.paper',
                                        bgcolor: 'primary.main',
                                    }}
                                >
                                    {(entity.canonicalName || '?')[0].toUpperCase()}
                                </Avatar>
                                {entityCanEdit ? (
                                    <>
                                        <input
                                            ref={avatarInputRef}
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleAvatarSelected}
                                        />
                                        <IconButton
                                            size="small"
                                            aria-label={displayImageUrl ? 'Change image' : 'Upload image'}
                                            disabled={avatarUploading}
                                            onClick={() => avatarInputRef.current?.click()}
                                            sx={{
                                                position: 'absolute',
                                                right: -4,
                                                bottom: -4,
                                                bgcolor: 'background.paper',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                '&:hover': {bgcolor: 'background.paper'},
                                            }}
                                        >
                                            {avatarUploading
                                                ? <CircularProgress size={16}/>
                                                : <PhotoCameraIcon fontSize="small"/>}
                                        </IconButton>
                                    </>
                                ) : null}
                            </Box>

                            <Stack spacing={0.5} flex={1} pb={0.5}>
                                <Typography variant="h4" fontWeight={700}>{entity.canonicalName}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    {entity.entityType && (
                                        <Chip label={getEntityTypeLabel(entity.entityType) || entity.entityType} color="primary" size="small"
                                              sx={{fontWeight: 600}}/>
                                    )}
                                    {!isLocalEntity && (entity.wikidataId || isWikidataQid) && (
                                        <Chip label="Wikidata Entity" size="small" variant="outlined" color="info"/>
                                    )}
                                    {isLocalEntity && entity.source !== 'entities' && (
                                        <Chip label="Custom Entity" size="small" variant="outlined"/>
                                    )}
                                    {isLocalEntity && entity.source === 'entities' && (
                                        <Chip label="Entity Candidate" size="small" variant="outlined" color="warning"/>
                                    )}
                                    {entity.confidence != null && (
                                        <Chip
                                            label={`${Math.round(entity.confidence * 100)}% confidence`}
                                            size="small" variant="outlined"
                                        />
                                    )}
                                    {!isLocalEntity && wikidataUrl && (
                                        <MuiLink
                                            href={wikidataUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13}}
                                        >
                                            {entity.wikidataId} <OpenInNewIcon sx={{fontSize: 14}}/>
                                        </MuiLink>
                                    )}
                                    {isLocalEntity && entity.wikidataId && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: 'monospace',
                                                color: 'text.disabled',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                px: 0.75,
                                                py: 0.25,
                                                lineHeight: 1.6,
                                                userSelect: 'all',
                                            }}
                                            title="Entity ID"
                                        >
                                            {entity.wikidataId}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>

                        {avatarError ? (
                            <Alert severity="error">
                                {avatarError.message || 'Failed to upload image.'}
                            </Alert>
                        ) : null}

                        <EntityDescriptionEditor
                            entityId={entityId}
                            entity={entity}
                            canEdit={entityCanEdit}
                            onSaved={() => setRefetchKey((value) => value + 1)}
                        />

                        {entity.aliases?.length > 0 && (
                            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                                {entity.aliases.map((alias, i) => (
                                    <Chip key={i} label={alias} variant="outlined" size="small"/>
                                ))}
                            </Stack>
                        )}

                        {/* ── Entity Candidate promote callout ── */}
                        {isNerCandidate && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    bgcolor: (t) => t.palette.mode === 'dark'
                                        ? 'rgba(255,167,38,0.08)'
                                        : 'warning.50',
                                    border: (t) => `1px solid ${t.palette.warning.light}`,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
                                    <AutoAwesomeIcon sx={{color: 'warning.main', fontSize: 20, flexShrink: 0}}/>
                                    <Box minWidth={0}>
                                        <Typography variant="body2" fontWeight={600} color="warning.dark">
                                            Auto-detected Entity Candidate
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Detected by the NER pipeline. Promote it to make it a first-class Custom Entity with editable properties, KB relationships, and full profile features.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Tooltip title="Review the name, type and description, then confirm to promote">
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        onClick={() => {
                                            setPromoteVariant('custom');
                                            setPromoteOpen(true);
                                        }}
                                        sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                                    >
                                        Promote to Custom Entity
                                    </Button>
                                </Tooltip>
                            </Box>
                        )}
                        {showAdminAdopt && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    bgcolor: (t) => t.palette.mode === 'dark'
                                        ? 'rgba(255,167,38,0.08)'
                                        : 'warning.50',
                                    border: (t) => `1px solid ${t.palette.warning.light}`,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
                                    <AutoAwesomeIcon sx={{color: 'warning.main', fontSize: 20, flexShrink: 0}}/>
                                    <Box minWidth={0}>
                                        <Typography variant="body2" fontWeight={600} color="warning.dark">
                                            Wikidata search Candidate
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            This is a real Wikidata item with no Knowledge Base type (and often no corpus links). Assign a type so Spotlight treats it as a Knowledge Base entity instead of a Candidate.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Tooltip title="Choose a NER type (Place, Person, Org, …) without changing the Q-ID">
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        onClick={() => {
                                            setPromoteVariant('wikidata');
                                            setPromoteOpen(true);
                                        }}
                                        sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                                    >
                                        Promote to Knowledge Base
                                    </Button>
                                </Tooltip>
                            </Box>
                        )}
                    </Stack>
                </Paper>

                {/* ── Tabs ── */}
                <Box sx={{mx: 2, mt: 2}}>
                    <Paper elevation={0}
                           sx={{borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}`, overflow: 'hidden'}}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => {
                                setTab(v);
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                px: 1,
                                pt: 1,
                                bgcolor: (t) => t.palette.action.hover,
                                borderBottom: 1,
                                borderColor: 'divider',
                                minHeight: 36,
                                '& .MuiTabs-indicator': {display: 'none'},
                                '& .MuiTabs-scrollButtons': {
                                    width: 24,
                                    color: 'text.secondary',
                                    '&.Mui-disabled': {opacity: 0.3},
                                },
                                '& .MuiTab-root': {
                                    minHeight: 30,
                                    minWidth: 0,
                                    mr: 0.25,
                                    px: 1.25,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    borderRadius: '8px 8px 0 0',
                                    border: (t) => `1px solid transparent`,
                                    borderBottom: 'none',
                                    transition: 'background-color .15s, color .15s',
                                    '&:hover': {
                                        bgcolor: (t) => t.palette.background.paper,
                                        color: 'text.primary',
                                    },
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    bgcolor: 'background.paper',
                                    color: 'primary.main',
                                    fontWeight: 700,
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                    borderBottom: (t) => `1px solid ${t.palette.background.paper}`,
                                    mb: '-1px',
                                },
                            }}
                        >
                            <Tab label="Summary"/>
                            <Tab label={tabLabel('In FINTEL', fintelClaims.length, fintelLoading)}/>
                            <Tab label={tabLabel('In Drafts', draftClaims.length, draftsLoading)}/>
                            <Tab label={tabLabel('Corpus', mentions.length, mentionsLoading)}/>
                            <Tab label={tabLabel('Files', dataItems.length, dataItemsLoading)}/>
                            <Tab label={tabLabel('Collection', collectionTotal, collectionLoading)}/>
                            <Tab label={tabLabel('Charts', entityLinkCharts.length, entityChartsLoading)}/>
                            <Tab label={tabLabel('Involvement', fintelClaims.length + draftClaims.length + mentions.length + dataItems.length, fintelLoading || draftsLoading || mentionsLoading || dataItemsLoading)}/>
                            <Tab label={tabLabel('Personnel', personnel.length, personnelLoading)}/>
                            <Tab label={tabLabel('Properties', properties.length, propsLoading || isFetchingProps)}/>
                            <Tab label={tabLabel('Alerts', alerts.length, alertsLoading)}/>
                            <Tab label={tabLabel('Aliases', aliasTabCount, aliasesLoading)}/>
                            {carverEligible && <Tab label="CARVER"/>}
                        </Tabs>

                        <Box>
                            {activeTab === 0 && (
                                <SummaryTab
                                    summary={intelligenceSummary}
                                    loading={intelligenceSummaryLoading}
                                    regenerating={intelligenceSummaryRegenerating}
                                    saving={intelligenceSummarySaving}
                                    error={intelligenceSummaryError}
                                    cached={intelligenceSummaryCached}
                                    stale={intelligenceSummaryStale}
                                    manuallyEdited={intelligenceSummaryManuallyEdited}
                                    generatedAt={intelligenceSummaryGeneratedAt}
                                    kbLastUpdatedAt={entity?.kbLastUpdatedAt || null}
                                    sourceCounts={intelligenceSourceCounts}
                                    onRegenerate={regenerateIntelligenceSummary}
                                    canEdit={summaryCanEdit}
                                    onSave={summaryCanEdit ? saveIntelligenceSummary : undefined}
                                    notes={notes}
                                    notesLoading={notesLoading}
                                    notesError={notesError}
                                    notesCanPost={notesCanPost}
                                    onPostNote={postNote}
                                    onUpdateNote={notesCanPost ? updateNote : undefined}
                                    onDeleteNote={notesCanPost ? deleteNote : undefined}
                                    notesSaving={notesSaving}
                                    notesMutationError={notesMutationError}
                                />
                            )}
                            {activeTab === 1 && (
                                <ClaimsTab
                                    claims={fintelClaims}
                                    reportHeadersById={fintelReportHeadersById}
                                    loading={fintelLoading}
                                    emptyMessage="This entity has no mentions in FINTEL reports."
                                    onAuthorClick={handleUserMentionClick}
                                    currentEntityWikidataId={currentEntityWikidataId}
                                />
                            )}
                            {activeTab === 2 && (
                                <ClaimsTab
                                    claims={draftClaims}
                                    reportHeadersById={draftReportHeadersById}
                                    loading={draftsLoading}
                                    emptyMessage="This entity has no mentions in draft reports."
                                    onAuthorClick={handleUserMentionClick}
                                    currentEntityWikidataId={currentEntityWikidataId}
                                />
                            )}
                            {activeTab === 3 && (
                                <MentionsTab
                                    mentions={mentions}
                                    loading={mentionsLoading}
                                    entityHighlightContext={entityHighlightContext}
                                />
                            )}
                            {activeTab === 4 && (
                                <DataItemsTab
                                    dataItems={dataItems}
                                    loading={dataItemsLoading}
                                    emptyMessage="This entity is not mentioned in any of your sources."
                                    entityHighlightContext={entityHighlightContext}
                                />
                            )}
                            {activeTab === 5 && (
                                <CollectionRequirementsTab
                                    icrs={collectionIcrs}
                                    eeis={collectionEeis}
                                    gaps={collectionGaps}
                                    cts={collectionCts}
                                    cms={collectionCms}
                                    iirs={collectionIirs}
                                    loading={collectionLoading}
                                    error={collectionError}
                                />
                            )}
                            {activeTab === 6 && (
                                <EntityChartsTab
                                    charts={entityLinkCharts}
                                    loading={entityChartsLoading}
                                    error={entityChartsError}
                                />
                            )}
                            {activeTab === 7 && (
                                <GraphTab
                                    entity={entity}
                                    fintelClaims={fintelClaims}
                                    draftClaims={draftClaims}
                                    fintelReportHeadersById={fintelReportHeadersById}
                                    draftReportHeadersById={draftReportHeadersById}
                                    mentions={mentions}
                                    dataItems={dataItems}
                                    loading={fintelLoading || draftsLoading || mentionsLoading || dataItemsLoading}
                                />
                            )}
                            {activeTab === 8 && (
                                <PersonnelTab
                                    personnel={personnel}
                                    loading={personnelLoading}
                                    error={personnelError}
                                />
                            )}
                            {activeTab === 9 && (
                                <Stack spacing={2}>
                                    {propertyError && (
                                        <Box p={2}>
                                            <Alert severity="error">
                                                {propertyError.message || 'Failed to update properties.'}
                                            </Alert>
                                        </Box>
                                    )}
                                    <PropertiesTab
                                        properties={properties}
                                        loading={propsLoading || isFetchingProps}
                                        onCreateProperty={propertyCrudEnabled ? handleCreateProperty : undefined}
                                        onUpdateProperty={propertyCrudEnabled ? handleUpdateProperty : undefined}
                                        onDeleteProperty={propertyCrudEnabled ? handleDeleteProperty : undefined}
                                        saving={propertyMutationSaving}
                                        error={propertyMutationError}
                                        crudEnabled={propertyCrudEnabled}
                                    />
                                </Stack>
                            )}
                            {activeTab === 10 && <AlertsTab alerts={alerts} loading={alertsLoading}/>}
                            {activeTab === 11 && (
                                <Stack spacing={2}>
                                    {aliasError && (
                                        <Box p={2}>
                                            <Alert severity="error">
                                                {aliasError.message || 'Failed to update aliases.'}
                                            </Alert>
                                        </Box>
                                    )}
                                    <AliasesTab
                                        wikidataAliases={wikidataAliases}
                                        surfaceForms={surfaceForms}
                                        loading={aliasesLoading}
                                        onCreateAlias={aliasCrudEnabled ? createAlias : undefined}
                                        onDeleteAlias={aliasCrudEnabled ? deleteAlias : undefined}
                                        saving={aliasMutationSaving}
                                        error={aliasMutationError}
                                        crudEnabled={aliasCrudEnabled}
                                    />
                                </Stack>
                            )}
                            {activeTab === 12 && carverEligible && (
                                <CarverTab
                                    factors={carverFactors}
                                    total={carverTotal}
                                    scoredFactorCount={carverScoredCount}
                                    minScore={carverMinScore}
                                    maxScore={carverMaxScore}
                                    loading={carverLoading}
                                    error={carverMutationError || carverError}
                                    saving={carverSaving}
                                    canEdit={carverCanEdit}
                                    onSetFactor={(factor, value) => {
                                        setCarverFactor(factor, value).catch(() => {
                                            /* surfaced through the hook's error state */
                                        });
                                    }}
                                    history={carverHistory}
                                    historyLoading={carverHistoryLoading}
                                />
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Stack>

            <UserMentionPopover
                open={mentionPopoverOpen}
                anchorEl={mentionAnchorEl}
                onClose={handleMentionPopoverClose}
                userData={mentionUserData}
            />

            <PromoteEntityCandidateDialog
                open={promoteOpen}
                onClose={() => setPromoteOpen(false)}
                entity={{...entity, wikidataId: entity?.wikidataId || entityId, id: entityId}}
                variant={promoteVariant}
                onPromoted={() => {
                    setRefetchKey(k => k + 1);
                    setPromoteSuccessMsg(promoteVariant === 'wikidata'
                        ? `"${entity?.canonicalName}" promoted to Knowledge Base`
                        : `"${entity?.canonicalName}" promoted to Custom Entity`);
                }}
            />

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => !deleteBusy && setDeleteConfirmOpen(false)}
                aria-labelledby="admin-delete-entity-title"
            >
                <DialogTitle id="admin-delete-entity-title">Remove from Knowledge Base?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Permanently delete{' '}
                        <strong>{entity?.canonicalName || entityId}</strong>
                        {entity?.wikidataId || entityId
                            ? ` (${entity?.wikidataId || entityId})`
                            : ''}{' '}
                        from the Knowledge Base. Mentions linked to this entity will be removed.
                        Report document marks are not stripped.
                        {(entity?.occurrenceCount ?? entity?.occurrence_count ?? 0) > 0
                            ? ` This entity has ${entity.occurrenceCount ?? entity.occurrence_count} mention(s).`
                            : ''}
                    </DialogContentText>
                    {deleteError ? (
                        <Alert severity="error" sx={{mt: 2}}>
                            {deleteError.message || 'Failed to delete entity.'}
                        </Alert>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteBusy}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleConfirmDeleteEntity}
                        disabled={deleteBusy}
                        startIcon={deleteBusy ? <CircularProgress size={16} color="inherit"/> : <DeleteOutlineIcon/>}
                        data-testid="admin-remove-entity-confirm"
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(promoteSuccessMsg)}
                autoHideDuration={4000}
                onClose={() => setPromoteSuccessMsg('')}
                message={promoteSuccessMsg}
            />
        </Stack>
    );
};

export default EntityDetailPage;

